import React from 'react';
import { Layout } from '../components/layout/Layout';
import { motion } from 'framer-motion';
import { Clock, CheckCircle } from 'lucide-react';
export function RefundPolicyPage() {
  return <Layout data-id="element-511">
      <div className="bg-gradient-to-br from-slate-50 to-teal-50 py-16" data-id="element-512">
        <div className="container mx-auto px-4 text-center" data-id="element-513">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-514">
            <h1 className="text-4xl font-bold text-slate-900 mb-4" data-id="element-515">
              Refund Policy
            </h1>
            <p className="text-slate-600 max-w-2xl mx-auto" data-id="element-516">
              Last updated:{' '}
              {new Date().toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            })}
            </p>
          </motion.div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-16" data-id="element-517">
        <div className="max-w-3xl mx-auto" data-id="element-518">
          <div className="prose prose-slate max-w-none" data-id="element-519">
            <p className="text-lg text-slate-600 leading-relaxed mb-8" data-id="element-520">
              At HomeLinkPro, we strive to provide excellent service. If you're
              not satisfied with your subscription, this refund policy explains
              your options based on when you request a refund.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 not-prose" data-id="element-521">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm" data-id="element-522">
                <div className="flex items-center gap-3 mb-4" data-id="element-523">
                  <div className="p-2 bg-green-100 text-green-600 rounded-lg" data-id="element-524">
                    <CheckCircle size={24} data-id="element-525" />
                  </div>
                  <h3 className="font-bold text-slate-900" data-id="element-526">0–14 Days</h3>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed" data-id="element-527">
                  Full refund available if the platform has not been used.
                </p>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.1
            }} className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm" data-id="element-528">
                <div className="flex items-center gap-3 mb-4" data-id="element-529">
                  <div className="p-2 bg-orange-100 text-orange-600 rounded-lg" data-id="element-530">
                    <Clock size={24} data-id="element-531" />
                  </div>
                  <h3 className="font-bold text-slate-900" data-id="element-532">15–28 Days</h3>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed" data-id="element-533">
                  Partial refund may be offered at our discretion.
                </p>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.2
            }} className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm" data-id="element-534">
                <div className="flex items-center gap-3 mb-4" data-id="element-535">
                  <div className="p-2 bg-slate-100 text-slate-600 rounded-lg" data-id="element-536">
                    <Clock size={24} data-id="element-537" />
                  </div>
                  <h3 className="font-bold text-slate-900" data-id="element-538">After 28 Days</h3>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed" data-id="element-539">
                  Refunds are not normally available.
                </p>
              </motion.div>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-540">
              Refund Timeframes
            </h2>

            <h3 className="text-xl font-semibold text-slate-900 mb-3" data-id="element-541">
              0–14 Days: Full Refund
            </h3>
            <p className="text-slate-600 leading-relaxed mb-6" data-id="element-542">
              If you request a refund within 14 days of your initial
              subscription purchase and you have not used the platform, you are
              entitled to a full refund. "Not used" means you have not logged
              in, created any data, uploaded files, or accessed any features of
              HomeLinkPro.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mb-3" data-id="element-543">
              15–28 Days: Partial Refund
            </h3>
            <p className="text-slate-600 leading-relaxed mb-6" data-id="element-544">
              Between 15 and 28 days after your subscription purchase, a partial
              refund may be offered at our discretion. The amount will depend on
              your level of usage and the circumstances of your request. Please
              contact our support team to discuss your situation.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mb-3" data-id="element-545">
              After 28 Days: No Refund
            </h3>
            <p className="text-slate-600 leading-relaxed mb-6" data-id="element-546">
              After 28 days from your subscription purchase, refunds are not
              normally available. You may cancel your subscription at any time
              to prevent future charges, and you will retain access until the
              end of your current billing period.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-547">
              How Refunds Are Processed
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4" data-id="element-548">
              All approved refunds are sent back to the original payment method
              used for the subscription:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-6" data-id="element-549">
              <li data-id="element-550">
                Refunds are processed within 5-10 working days of approval
              </li>
              <li data-id="element-551">
                The refund will appear on your bank statement or card account
                within 5-10 working days after processing
              </li>
              <li data-id="element-552">
                You will receive an email confirmation once the refund has been
                processed
              </li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-553">
              How to Request a Refund
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4" data-id="element-554">
              To request a refund, please contact our support team:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-6" data-id="element-555">
              <li data-id="element-556">
                Email:{' '}
                <a href="mailto:lbhuwan151@gmail.com" className="text-teal-600 hover:text-teal-700" data-id="element-557">
                  lbhuwan151@gmail.com
                </a>
              </li>
              <li data-id="element-558">
                Include your account email address and reason for the refund
                request
              </li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-559">
              Exceptions
            </h2>
            <p className="text-slate-600 leading-relaxed mb-6" data-id="element-560">
              Refunds will not be issued in cases of:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-6" data-id="element-561">
              <li data-id="element-562">Violation of our Terms & Conditions</li>
              <li data-id="element-563">Misuse or abuse of the platform</li>
              <li data-id="element-564">
                Chargebacks or payment disputes initiated without contacting us
                first
              </li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-565">
              Questions?
            </h2>
            <p className="text-slate-600 leading-relaxed" data-id="element-566">
              If you have any questions about this refund policy, please contact
              us at{' '}
              <a href="mailto:lbhuwan151@gmail.com" className="text-teal-600 hover:text-teal-700" data-id="element-567">
                lbhuwan151@gmail.com
              </a>{' '}
              or visit our{' '}
              <a href="/contact" className="text-teal-600 hover:text-teal-700" data-id="element-568">
                Contact Us
              </a>{' '}
              page.
            </p>
          </div>
        </div>
      </div>
    </Layout>;
}